/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { useDispatch } from "react-redux";
import { Button } from "@emisgroup/ui-button";
import { NoExecutionIcon } from "../../Execution/ExecutionList/NoExecutionIcon";
import { SummaryView } from "../../Execution/ExecutionDetail/SummaryView";
import { IExecutionConfiguration } from "../../Execution/ExecutionDetail/Types";
import { useExecutionConfiguration } from "../../../../service/hooks";
import { ErrorAlertSlice } from "../../../ErrorAlert/Index";
import { Insights } from "../Insights/Insights";
import { CodeListView } from "../CodeListView/CodeListView";
import { Rules } from "../CodeListView/Rules/Rules";
import { CodeDetails } from "../CodeListView/CodeDetails/CodeDetails";
import { NoDetails } from "../../../Common/NoDetails/NoDetails";
import { SearchReportView } from "../SearchReport/SearchReportView";
import { PrescriptionIcon } from "./PrescriptionIcon";
import "./Style.scss";

export enum ResultView {
  Default = "Default",
  CodeListView = "CodeListView",
  SearchReport = "SearchReport"
}

type NoResult = { title: string; description: string };

const checkForNoResult = (
  artefactId: string,
  executionConfiguration: IExecutionConfiguration | undefined): NoResult | undefined => {

  if(!executionConfiguration) {
    return {
      title: "ResultDetails.NoExecutionSelected.Title",
      description: "ResultDetails.NoExecutionSelected.Description"
    };
  }

  if(artefactId === executionConfiguration.artefactId && executionConfiguration.status !== "Completed") {
    return {
      title: "ResultDetails.NotCompleted.Title",
      description: "ResultDetails.NotCompleted.Description"
    };
  }

  return;
};

export const ResultDetail: React.FC = () => {
  const { t: translate } = useTranslation();
  const artefactId = sessionStorage.getItem("selectedArtefactId") as string;
  const executionId = sessionStorage.getItem("selectedExecutionId") as string;
  const executionConfigurationId = sessionStorage.getItem(artefactId)
    ?? sessionStorage.getItem("selectedExecutionConfigurationId") ?? "";
  const dispatch = useDispatch();
  const [view, switchView] = useState<string>(ResultView.Default);

  const [executionConfiguration, setExecutionConfiguration] = useState<IExecutionConfiguration>();

  const executionRequest = useExecutionConfiguration(executionConfigurationId);

  const getExecutionConfiguration = (): void => {
    if (executionRequest.error) {
      dispatch(ErrorAlertSlice.actions.createErrorAlert({
        title: "Error",
        message: [executionRequest.error["errors"][0].message || translate("UnknownError")],
        type: "error"
      }));
    } else {
      const data = executionRequest.data && executionRequest.data.data;
      const ecData: IExecutionConfiguration = data as IExecutionConfiguration;
      if (ecData) {
        setExecutionConfiguration(structuredClone(ecData));
      }
    }
  };

  useEffect(() => {
    void getExecutionConfiguration();
  }, [executionRequest.isLoading]);

  const noResult = checkForNoResult(artefactId, executionConfiguration);

  if(executionRequest.isLoading || executionConfiguration === undefined) {
    return noResult && <NoDetails
      style={{ paddingTop: "20vh" }}
      title={translate(noResult.title)}
      description={translate(noResult.description)}
      notFoundIcon={ <NoExecutionIcon/> }
    />;
  }

  const isDisabledSearchReportButton = ["Draft", "Running", "Failed"].includes(executionConfiguration.status);

  const hasOptOutElement = Array.from(
    document.querySelectorAll('[data-testid="ebt-extract-navigation"]')
  ).find(el => el?.textContent?.includes("OptOut"));

  const views = {
    [ResultView.Default]: (
      <div className="result-detail__info">
        <div className="result-detail__info__data">
          <div className="result-detail__name">
            <h2>{executionConfiguration.name}</h2>

            <Button
              borderless={true}
              onClick={(): void =>
                hasOptOutElement
                  ? switchView(ResultView.SearchReport)
                  : switchView(ResultView.CodeListView)
              }
              data-testid={hasOptOutElement ? "viewSearchReport" : "viewCodeList"}
              disabled={hasOptOutElement ? isDisabledSearchReportButton : false}
            >
              <PrescriptionIcon/>
              {hasOptOutElement
                ? translate("ResultDetails.SearchReport.ViewSearchReport")
                : translate("ResultDetails.CodeList.ViewCodeList")
              }
            </Button>
          </div>
          <SummaryView
            executionConfiguration={ executionConfiguration }
            showingRunDates={false}
            showSelection= { true }
          />
        </div>
        <div className="result-detail__info__insights">
          <Insights executionType={executionConfiguration.typeValue}/>
        </div>
      </div>
    ),
    [ResultView.CodeListView]:
      <CodeListView
        leftSide= {
          <Rules
            executionId={executionId}
            executionName={executionConfiguration.name}
            hasOptOutElement={!!hasOptOutElement}
            artefactId={executionConfiguration.artefactId}
            onBack={(): void => {
              switchView(hasOptOutElement ? ResultView.SearchReport : ResultView.Default);
            }}/>
        }
        rightSide= { <CodeDetails isOlderThanFiveDays={executionConfiguration.isOlderThanFiveDays}/> }
      />,
    [ResultView.SearchReport]:
      <SearchReportView
        cohortExecutionId={executionConfiguration.cohortExecutionId}
        executionName={executionConfiguration.name}
        onBack={(): void => switchView(ResultView.Default)}
        setSwitchView={switchView}
      />,
  };

  return (
    <div className="result-detail">
      {
        noResult && <NoDetails
          style={{ paddingTop: "20vh" }}
          title={translate(noResult.title)}
          description={translate(noResult.description)}
          notFoundIcon={ <NoExecutionIcon/>}
        />
      }
      {
        executionConfiguration.status === "Completed" && views[view]
      }
    </div>
  );
};
